import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchSession } from '../auth/googleSession'
import { sendAreaQuestion } from '../transport/api'
import { Icon } from '../ui/Icon'
import { AgentMention, findMention, pingTag, type MentionAgent } from '../agents/AgentMention'
import { useVoiceRecorder } from '../voice/useVoiceRecorder'
import { registerMicHotkey, MIC_HOTKEY_LABEL } from '../voice/micHotkey'
import { transcribeBlob } from '../voice/transcribe'
import { insertAtCaret } from '../voice/insertAtCaret'
import { micGate, recordCapMs, reportVoiceError } from '../billing/voiceGate'
import { boxHeight, isSendKey } from './BoardChat'
import { clearsAfterSend, setClearsAfterSend } from './area'
import type { Box } from './model'

/**
 * THE QUESTION'S WINDOW — what opens beside an area the person has boxed (board/area.ts; owner, 2026-10-31: 選擇之後呢,
 * 就會跳出來一個小的對話框, 使用者就可以使用文字輸入, 或者是使用麥克風轉文字的方式輸入).
 *
 * Typed or spoken (the composer's microphone: record, transcribe, insert at the caret), `@` for one of the room's
 * agents as in the board's chat, and Send. What it sends is the words, the box, the items in it, and a picture of it
 * (chat/projectDynamo dynamoSendAreaQuestion). "Clear after sending" is remembered on this device: on, the box
 * leaves the board once the question has gone; off, it stays until hidden or deleted.
 */
export function AskBox({
  convId,
  board,
  boardName,
  area,
  ids,
  agents,
  style,
  picture,
  onSent,
  onCancel,
}: {
  convId: string
  board: string
  boardName: string
  area: Box
  /** The items seen in the area (area.ts itemsIn). */
  ids: string[]
  agents: MentionAgent[]
  /** Where it stands (BoardCanvas places it beside the box; on a phone, along the bottom). */
  style: CSSProperties
  /** The area's picture, made when Send is pressed (areaPicture.ts) — null when this browser cannot. */
  picture: () => Promise<Blob | null>
  onSent: (sent: { id: string; text: string; keep: boolean }) => void
  onCancel: () => void
}) {
  const me = useQuery({ queryKey: ['auth-me'], queryFn: fetchSession, staleTime: Infinity })
  const who = me.data?.kind === 'in' ? me.data.identity : null
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clear, setClear] = useState(clearsAfterSend)
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  const draftRef = useRef(draft)
  draftRef.current = draft

  const syncMention = (el: HTMLTextAreaElement) => setMention(findMention(el.value, el.selectionStart ?? el.value.length))
  const caretTo = (caret: number) =>
    requestAnimationFrame(() => {
      const el = box.current
      if (!el) return
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  const insertMention = (a: MentionAgent) => {
    if (!mention) return
    const before = draft.slice(0, mention.start)
    const after = draft.slice(mention.start + 1 + mention.query.length)
    const tag = pingTag(a)
    setDraft(`${before}${tag}${after}`)
    setMention(null)
    caretTo(before.length + tag.length)
  }
  const startMention = () => {
    const el = box.current
    if (!el) return
    const at = el.selectionStart ?? draft.length
    const lead = at > 0 && !/\s/.test(draft[at - 1] ?? '') ? ' @' : '@'
    setDraft(`${draft.slice(0, at)}${lead}${draft.slice(at)}`)
    setMention({ start: at + lead.length - 1, query: '' })
    caretTo(at + lead.length)
  }

  // Spoken words go in at the caret, as the composer's and a note's microphone put them.
  const voice = useVoiceRecorder({
    onClip: async (blob, durationMs) => {
      try {
        const r = await transcribeBlob(blob, durationMs)
        if (!r.text.trim()) {
          setError('No speech was detected.')
          return
        }
        const cur = draftRef.current
        const el = box.current
        const next = insertAtCaret(cur, el?.selectionStart ?? cur.length, el?.selectionEnd ?? cur.length, r.text)
        setDraft(next.value)
        setError(null)
        caretTo(next.caret)
      } catch (e) {
        reportVoiceError(e, setError)
      }
    },
    onProblem: setError,
    maxMs: recordCapMs,
  })
  const micPress = () => {
    if (micGate(voice.state)) return
    voice.toggle()
  }
  // While the window is open, Ctrl+M is ITS microphone (micHotkey's stack hands the key back when it closes).
  const micRef = useRef(micPress)
  micRef.current = micPress
  useEffect(() => registerMicHotkey(() => micRef.current()), [])
  const micRec = voice.state === 'recording'
  const micBusy = voice.state === 'starting' || voice.state === 'uploading' || voice.state === 'stopping'
  const secs = Math.floor(voice.elapsedMs / 1000)

  useEffect(() => {
    box.current?.focus()
  }, [])
  // One line to five, as the board's chat box grows (BoardChat boxHeight).
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const cs = getComputedStyle(el)
    const line = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.35 || 21
    const padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0)
    el.style.height = 'auto'
    const { height, scroll } = boxHeight(el.scrollHeight, line, padding, border)
    el.style.height = `${height}px`
    el.style.overflowY = scroll ? 'auto' : 'hidden'
  }, [draft])

  const send = async () => {
    const text = draft.trim()
    if (!text || sending) return
    setSending(true)
    setError(null)
    setMention(null)
    try {
      const pic = await picture().catch(() => null)
      const row = await sendAreaQuestion(convId, text, { id: board, name: boardName }, { ...area, ids }, pic, {
        email: who?.email ?? null,
        name: who?.name ?? null,
        picture: who?.picture ?? null,
      })
      onSent({ id: row.message_id, text, keep: !clear })
    } catch (e) {
      setSending(false)
      setError(sendRefusal(e instanceof Error ? e.message : ''))
    }
  }

  const count = ids.length
  return (
    <form
      className="bd-ask"
      style={style}
      role="dialog"
      aria-label="Ask about this area"
      onPointerDown={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault()
        void send()
      }}
    >
      <div className="bd-ask-head">
        <AskGlyph size={16} />
        <span className="bd-ask-title">Ask about this area</span>
        <span className="bd-ask-count">{count === 0 ? 'nothing drawn in it' : `${count} item${count === 1 ? '' : 's'}`}</span>
        <button type="button" className="bd-ask-x" aria-label="Cancel the question" onClick={onCancel}>
          <Icon name="close" size={16} />
        </button>
      </div>
      {mention && <AgentMention agents={agents} query={mention.query} onPick={insertMention} onDismiss={() => setMention(null)} />}
      <div className="bd-ask-row">
        <textarea
          ref={box}
          rows={1}
          value={draft}
          disabled={sending}
          placeholder="What do you want to know about it?"
          aria-label="Your question about this area"
          onChange={(e) => {
            setDraft(e.target.value)
            syncMention(e.target)
          }}
          onKeyUp={(e) => syncMention(e.currentTarget)}
          onClick={(e) => syncMention(e.currentTarget)}
          onKeyDown={(e) => {
            // The board's own keys (V, Delete, arrows…) are not this box's.
            e.stopPropagation()
            if (e.defaultPrevented) return
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
              return
            }
            if (isSendKey({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing, keyCode: e.keyCode })) {
              e.preventDefault()
              void send()
            }
          }}
        />
      </div>
      <div className="bd-ask-foot">
        <button type="button" className="bd-ask-icon" aria-label="Mention an agent" title="Mention an agent (@)" disabled={sending} onMouseDown={(e) => e.preventDefault()} onClick={startMention}>
          @
        </button>
        <button
          type="button"
          className={`bd-ask-icon mic${micRec ? ' rec' : micBusy ? ' busy' : ''}`}
          disabled={micBusy || sending}
          onClick={micPress}
          aria-label={micRec ? 'Stop recording' : 'Speak your question'}
          aria-pressed={micRec}
          title={micRec ? `Stop recording and insert the words (${MIC_HOTKEY_LABEL})` : voice.state === 'uploading' ? 'Transcribing…' : `Speak your question (${MIC_HOTKEY_LABEL})`}
        >
          {micRec ? <span className="mic-clock">{`${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`}</span> : <Icon name="mic" size={18} />}
        </button>
        <label className="bd-ask-clear" title="On: the box leaves the board once the question is sent. Off: it stays until you hide or delete it.">
          <input
            type="checkbox"
            checked={clear}
            onChange={(e) => {
              setClear(e.target.checked)
              setClearsAfterSend(e.target.checked)
            }}
          />
          <span>Clear after sending</span>
        </label>
        <button type="submit" className="bd-ask-send" disabled={!draft.trim() || sending}>
          {sending ? 'Sending…' : 'Send'}
          {!sending && <Icon name="send" size={16} />}
        </button>
      </div>
      {error && (
        <div className="bd-ask-err" role="alert">
          {error}
        </div>
      )}
    </form>
  )
}

/** A refused question, in words. */
function sendRefusal(code: string): string {
  if (code === 'not_a_member') return 'You are no longer in this project.'
  if (code === 'area_invalid' || code === 'board_invalid' || code === 'invalid_send') return 'This project cannot take questions about an area yet.'
  if (code === 'send_timeout') return 'Not sent — the connection did not answer. Try again.'
  return 'Not sent — check the connection and try again.'
}

/**
 * The tool's mark: a dashed box with a speech bubble at its corner (owner: 一個 Chat, 再加上一個小虛線框框的這種圖示).
 */
export function AskGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.7}>
      <rect x="2.75" y="2.75" width="14" height="14" rx="2" strokeDasharray="2.6 2.2" />
      <path
        d="M13.5 11.5h7a1.5 1.5 0 0 1 1.5 1.5v4.5a1.5 1.5 0 0 1-1.5 1.5h-3.1l-2.6 2.2v-2.2h-1.3a1.5 1.5 0 0 1-1.5-1.5V13a1.5 1.5 0 0 1 1.5-1.5Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}
